import { useEffect, useRef, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { ShieldCheck, ShieldOff, Trash2, Upload, Pencil, Check, X, FileUp, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { reviews as reviewsApi, testimonials as testimonialsApi } from '../api/endpoints'
import type { ProductReview } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { PageLoader } from '../components/Spinner'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Textarea } from '../components/Input'
import { parseCSV } from '../lib/parseCSV'

interface PickerProduct { id: string; name: string; slug: string; categoryName: string | null }

export function ReviewsPage() {
  const [data, setData] = useState<ProductReview[]>([])
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Inline editing state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editRating, setEditRating] = useState(5)
  const [editTitle, setEditTitle] = useState('')
  const [editBody, setEditBody] = useState('')

  // Single-row create state
  const [creating, setCreating] = useState(false)
  const [picker, setPicker] = useState<PickerProduct[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)
  const [newProductId, setNewProductId] = useState('')
  const [newRating, setNewRating] = useState(5)
  const [newAuthor, setNewAuthor] = useState('')
  const [newLocation, setNewLocation] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [newVerified, setNewVerified] = useState(true)
  const [saving, setSaving] = useState(false)

  function reload() {
    setLoading(true)
    reviewsApi
      .list({})
      .then((r) => setData(r.reviews))
      .catch((err) => toast.error('Failed to load reviews', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  async function toggleVerify(review: ProductReview) {
    if (review.verified) return
    try {
      await reviewsApi.approve(review.id)
      toast.success('Review verified')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  function startEdit(r: ProductReview) {
    setEditingId(r.id)
    setEditRating(r.rating)
    setEditTitle(r.title)
    setEditBody(r.body)
  }

  async function saveEdit() {
    if (!editingId) return
    try {
      await reviewsApi.update(editingId, { rating: editRating, title: editTitle, body: editBody })
      toast.success('Review updated')
      setEditingId(null)
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this review?')) return
    try {
      await reviewsApi.delete(id)
      toast.success('Review deleted')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  /* Lazy-load the product picker the first time the dialog opens.
   * Reuses the testimonials endpoint — same needs, same shape.
   * Module-level cache on the server keeps the round-trip cheap. */
  function loadPicker() {
    if (picker.length > 0 || pickerLoading) return
    setPickerLoading(true)
    testimonialsApi.picker()
      .then((r) => setPicker(r.products ?? []))
      .catch(() => toast.error('Failed to load products', { description: 'Check your connection and try again.' }))
      .finally(() => setPickerLoading(false))
  }

  function resetCreateForm() {
    setNewProductId(''); setNewRating(5); setNewAuthor(''); setNewLocation('')
    setNewTitle(''); setNewBody(''); setNewVerified(true)
  }

  function openCreate() {
    resetCreateForm()
    loadPicker()
    setCreating(true)
  }

  async function handleCreate() {
    setSaving(true)
    try {
      /* productId accepts slug or UUID — the server's lookup
       * resolves to the real product id before persisting. */
      await reviewsApi.create({
        productId: newProductId,
        author: newAuthor,
        location: newLocation || undefined,
        rating: newRating,
        title: newTitle,
        body: newBody,
        verified: newVerified,
      })
      toast.success('Review created')
      setCreating(false)
      resetCreateForm()
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally { setSaving(false) }
  }

  function handleFilePick(file: File) {
    setCsvFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      setCsvText(typeof reader.result === 'string' ? reader.result : '')
    }
    reader.onerror = () => {
      toast.error('Failed to read file')
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    // Use the proper CSV parser that handles quoted fields
    const rows = parseCSV(csvText)
    if (rows.length === 0) {
      toast.error('CSV must have a header row + at least one data row')
      return
    }

    const requiredHeaders = ['productid', 'author', 'rating', 'title', 'body']
    const missing = requiredHeaders.filter((h) => !(h in rows[0]))
    if (missing.length > 0) {
      toast.error(`Missing columns: ${missing.join(', ')}`)
      return
    }

    const entries = rows
      .map((row) => ({
        productId: row.productid,
        author: row.author,
        location: row.location || '',
        rating: Math.max(1, Math.min(5, parseInt(row.rating, 10) || 5)),
        title: row.title,
        body: row.body,
        date: row.date || undefined,
      }))
      .filter((e) => e.productId && e.author && e.title && e.body)

    if (entries.length === 0) { toast.error('No valid rows found'); return }

    setImporting(true)
    try {
      const result = await reviewsApi.import(entries)
      toast.success(`Imported ${result.count} reviews`)
      setImportOpen(false)
      setCsvText('')
      setCsvFileName(null)
      reload()
    } catch (err) {
      toast.error('Import failed', { description: (err as Error).message })
    } finally { setImporting(false) }
  }

  function stars(n: number) {
    return editingId ? (
      <select value={n} onChange={(e) => setEditRating(Number(e.target.value))} className="text-amber font-bold bg-transparent border border-amber rounded px-1 py-0.5 text-xs">
        {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{'★'.repeat(v)}</option>)}
      </select>
    ) : '★'.repeat(n) + '☆'.repeat(5 - n)
  }

  const columns: ColumnDef<ProductReview>[] = [
    { header: 'Product', accessorKey: 'productName', cell: ({ getValue }) => <span className="font-medium text-sm">{String(getValue())}</span> },
    { header: 'Author', accessorKey: 'author', cell: ({ getValue }) => <span className="text-sm">{String(getValue())}</span> },
    {
      header: 'Rating',
      accessorKey: 'rating',
      cell: ({ getValue, row }) => (
        editingId === row.original.id
          ? stars(editRating)
          : <span className="text-amber font-bold">{stars(Number(getValue()))}</span>
      ),
    },
    {
      header: 'Title',
      accessorKey: 'title',
      cell: ({ getValue, row }) =>
        editingId === row.original.id
          ? <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="text-xs py-1 h-7" />
          : <span className="text-sm line-clamp-1 max-w-[200px]">{String(getValue())}</span>,
    },
    {
      header: 'Body',
      accessorKey: 'body',
      cell: ({ getValue, row }) =>
        editingId === row.original.id
          ? <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className="text-xs py-1 h-12" rows={2} />
          : <span className="text-xs text-slatey line-clamp-1 max-w-[200px]">{String(getValue())}</span>,
    },
    {
      header: 'Verified',
      accessorKey: 'verified',
      cell: ({ row }) => (
        <button onClick={() => toggleVerify(row.original)} className="inline-flex items-center gap-1">
          {row.original.verified ? <ShieldCheck className="size-4 text-forest" /> : <ShieldOff className="size-4 text-slatey hover:text-forest" />}
        </button>
      ),
    },
    { header: 'Helpful', accessorKey: 'helpful', cell: ({ getValue }) => <span className="tabular-nums text-sm">{String(getValue())}</span> },
    { header: 'Date', accessorKey: 'date', cell: ({ getValue }) => <span className="text-xs text-slatey">{String(getValue())}</span> },
    {
      header: 'Actions',
      cell: ({ row }) =>
        editingId === row.original.id ? (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={saveEdit} className="text-forest"><Check className="size-3.5" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="text-slatey"><X className="size-3.5" /></Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => startEdit(row.original)}><Pencil className="size-3.5" /></Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(row.original.id)}><Trash2 className="size-3.5" /></Button>
          </div>
        ),
    },
  ]

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink">Product Reviews</h1>
          <p className="text-sm text-slatey mt-1">{data.length} review{data.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openCreate}><Plus className="size-4" />Add Review</Button>
          <Button onClick={() => setImportOpen(true)} variant="outline"><Upload className="size-4" />Import CSV</Button>
        </div>
      </div>

      <DataTable data={data} columns={columns} emptyTitle="No reviews yet" emptyDescription="Customer reviews will appear here. Use Import CSV to bulk-add reviews." />

      {creating && (
        <Dialog open onOpenChange={() => { setCreating(false) }}>
          <DialogContent title="Add Review">
            <div className="space-y-4">
              <FormField label="Product" required>
                <select
                  className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                  value={newProductId}
                  onChange={(e) => setNewProductId(e.target.value)}
                  disabled={pickerLoading}
                >
                  <option value="">{pickerLoading ? 'Loading products…' : '— Select product —'}</option>
                  {picker.map((p) => (
                    <option key={p.id} value={p.slug}>
                      {p.categoryName ? `${p.name} (${p.categoryName})` : p.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Rating (1-5)" required>
                  <select
                    className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                    value={newRating}
                    onChange={(e) => setNewRating(parseInt(e.target.value, 10))}
                  >
                    {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{'★'.repeat(v)}</option>)}
                  </select>
                </FormField>
                <FormField label="Verified">
                  <select
                    className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
                    value={newVerified ? 'true' : 'false'}
                    onChange={(e) => setNewVerified(e.target.value === 'true')}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </FormField>
              </div>
              <FormField label="Author" required>
                <Input value={newAuthor} onChange={(e) => setNewAuthor(e.target.value)} placeholder="Rajesh Kumar" />
              </FormField>
              <FormField label="Location">
                <Input value={newLocation} onChange={(e) => setNewLocation(e.target.value)} placeholder="Delhi" />
              </FormField>
              <FormField label="Title" required>
                <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Helped my digestion in 2 weeks" />
              </FormField>
              <FormField label="Body" required>
                <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} rows={4} placeholder="What stood out — taste, results, anything you wish you knew before buying?" />
              </FormField>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <DialogClose asChild><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button></DialogClose>
              <Button
                onClick={handleCreate}
                disabled={saving || !newProductId || !newAuthor.trim() || !newTitle.trim() || !newBody.trim()}
              >
                {saving ? 'Saving…' : 'Create review'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {importOpen && (
        <Dialog open onOpenChange={() => { setImportOpen(false); setCsvFileName(null); setCsvText('') }}>
          <DialogContent title="Import Reviews from CSV">
            <p className="text-sm text-slatey mb-3">
              Upload a CSV file or paste content below. Columns: productId, author, location, rating, title, body, date
            </p>

            <div className="mb-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFilePick(file)
                  // Reset so re-picking the same file fires onChange again
                  e.target.value = ''
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="size-4" />
                {csvFileName ? csvFileName : 'Choose CSV file'}
              </Button>
              {csvFileName ? (
                <button
                  type="button"
                  onClick={() => { setCsvFileName(null); setCsvText('') }}
                  className="ml-2 text-xs text-danger hover:underline"
                >
                  Clear file
                </button>
              ) : null}
            </div>

            <Textarea
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value)
                setCsvFileName(e.target.value ? '(pasted)' : null)
              }}
              rows={12}
              placeholder={`productid,author,location,rating,title,body,date\nabc-123,Rajesh Kumar,Delhi,5,Great product,"Really works, no side effects",2026-07-01`}
              className="font-mono text-xs"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setImportOpen(false); setCsvFileName(null); setCsvText('') }}>Cancel</Button>
              <Button onClick={handleImport} disabled={importing || !csvText.trim()}>
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
